/**
 * Account page: who the account is (header), where it stands (status, health, next step), the
 * work in flight (deals, tickets), the people, and the reference details the team looks up
 * (address, phone, PM software, former names, Pipedrive ID), plus the client's commercial
 * terms on their own tab. Account details are edited in the zInspector dashboard
 * (zcrm-docs/reference/account-fields.md).
 */
import { ExternalLink, Star } from "lucide-react";
import {
  ShowBase,
  useGetList,
  useListContext,
  useShowContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router";

import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { findDealLabel } from "../deals/dealUtils";
import type { ZcrmAccount } from "../providers/zinspector/accounts";
import type { ZcrmTicket } from "../providers/zinspector/mappers";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact, Deal, Sale } from "../types";
import {
  AccountHealth,
  AccountKind,
  AccountStatus,
  EmptyValue,
  formatDoors,
  LastTouch,
  NextStep,
} from "./accountDisplay";
import { EditableCell } from "./EditableCell";
import { CallsTab } from "../calls/CallsTab";
import { CommercialTab } from "./CommercialTab";

export const AccountShow = () => (
  <ShowBase>
    <AccountShowContent />
  </ShowBase>
);

const TABS = ["overview", "calls", "commercial"] as const;
type AccountTab = (typeof TABS)[number];

const AccountShowContent = () => {
  const { record: account, isPending } = useShowContext<ZcrmAccount>();
  const translate = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();
  if (isPending || !account) return null;
  const requested = searchParams.get("tab") as AccountTab | null;
  const tab = requested && TABS.includes(requested) ? requested : "overview";

  return (
    <div className="mt-2 flex flex-col gap-4">
      <AccountHeader account={account} />
      <Tabs
        value={tab}
        onValueChange={(value) =>
          setSearchParams(value === "overview" ? {} : { tab: value }, {
            replace: true,
          })
        }
      >
        <TabsList>
          <TabsTrigger value="overview">
            {translate("zcrm.accounts.tabs.overview", { _: "Overview" })}
          </TabsTrigger>
          <TabsTrigger value="calls">
            {translate("zcrm.accounts.tabs.calls", { _: "Calls" })}
          </TabsTrigger>
          <TabsTrigger value="commercial">
            {translate("zcrm.accounts.tabs.commercial", { _: "Commercial" })}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <AccountOverview account={account} />
        </TabsContent>
        <TabsContent value="calls">
          <CallsTab account={account} />
        </TabsContent>
        <TabsContent value="commercial">
          <CommercialTab account={account} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const AccountOverview = ({ account }: { account: ZcrmAccount }) => (
  <div className="flex flex-col lg:flex-row gap-4 items-start">
    <div className="flex-1 flex flex-col gap-4 min-w-0 w-full">
      <Section title="resources.companies.fields.next_step">
        <EditableCell
          account={account}
          label="Next step"
          spec={{ type: "nextStep" }}
        >
          <NextStep account={account} />
        </EditableCell>
      </Section>
      <Section title="resources.deals.name" titleOptions={{ smart_count: 2 }}>
        <ReferenceManyField
          reference="deals"
          target="company_id"
          sort={{ field: "updated_at", order: "DESC" }}
        >
          <DealRows />
        </ReferenceManyField>
      </Section>
      <Section title="resources.tickets.name" titleOptions={{ smart_count: 2 }}>
        <ReferenceManyField
          reference="tickets"
          target="company_id"
          sort={{ field: "updated_at", order: "DESC" }}
        >
          <TicketRows />
        </ReferenceManyField>
      </Section>
      <Section
        title="resources.contacts.name"
        titleOptions={{ smart_count: 2 }}
      >
        <ReferenceManyField
          reference="contacts"
          target="company_id"
          sort={{ field: "last_name", order: "ASC" }}
        >
          <ContactRows mainContactId={account.main_contact_id} />
        </ReferenceManyField>
      </Section>
    </div>
    <div className="w-full lg:w-80 flex flex-col gap-4">
      <ContactDetails account={account} />
      <EngagementDetails account={account} />
      <ReferenceDetails account={account} />
    </div>
  </div>
);

const AccountHeader = ({ account }: { account: ZcrmAccount }) => {
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "first_name", order: "ASC" },
  });
  const people = sales.map((sale) => ({
    value: String(sale.id),
    label: `${sale.first_name} ${sale.last_name}`.trim(),
  }));
  const translate = useTranslate();
  const doors = formatDoors(account);
  const facts: [string, ReactNode][] = [
    [
      "status",
      <EditableCell
        account={account}
        label={translate("resources.companies.fields.status")}
        spec={{ type: "status" }}
      >
        <AccountStatus account={account} />
      </EditableCell>,
    ],
    ["health", <AccountHealth value={account.health} />],
    ["market", account.market || <EmptyValue />],
    ["doors", doors || <EmptyValue />],
    [
      "account_manager_id",
      <EditableCell
        account={account}
        label={translate("resources.companies.fields.account_manager_id")}
        spec={{
          type: "select",
          field: "account_manager_id",
          options: people,
        }}
      >
        {people.find((p) => p.value === String(account.account_manager_id))
          ?.label ?? <EmptyValue />}
      </EditableCell>,
    ],
    ["last_touch", <LastTouch date={account.last_touch} />],
  ];
  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold">{account.name}</h1>
          <AccountKind value={account.account_kind} />
          {account.accounting_id && (
            <span className="text-sm text-muted-foreground">
              {translate("resources.companies.fields.accounting_id")}{" "}
              {account.accounting_id}
            </span>
          )}
        </div>
        <dl className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {facts.map(([field, value]) => (
            <div key={field} className="flex flex-col gap-1 min-w-0">
              <dt className="text-xs text-muted-foreground">
                {translate(`resources.companies.fields.${field}`)}
              </dt>
              <dd className="text-sm">{value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
};

const Section = ({
  title,
  titleOptions,
  children,
}: {
  title: string;
  titleOptions?: Record<string, unknown>;
  children: ReactNode;
}) => {
  const translate = useTranslate();
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold">
          {translate(title, titleOptions)}
        </h2>
        {children}
      </CardContent>
    </Card>
  );
};

const NoneYet = () => (
  <p className="text-sm text-muted-foreground">None yet.</p>
);

const DealRows = () => {
  const { data: deals, isPending } = useListContext<Deal>();
  const { dealStages } = useConfigurationContext();
  if (isPending) return null;
  if (!deals?.length) return <NoneYet />;
  return (
    <ul className="divide-y">
      {deals.map((deal) => (
        <li key={deal.id}>
          <Link
            to={`/deals/${deal.id}/show`}
            className="flex justify-between gap-4 py-2 text-sm hover:bg-muted/50"
          >
            <span className="truncate">{deal.name}</span>
            <span className="text-muted-foreground shrink-0">
              {findDealLabel(dealStages, deal.stage) ?? deal.stage}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
};

const TicketRows = () => {
  const { data: tickets, isPending } = useListContext<ZcrmTicket>();
  if (isPending) return null;
  if (!tickets?.length) return <NoneYet />;
  return (
    <ul className="divide-y">
      {tickets.map((ticket) => (
        <li key={ticket.id} className="flex justify-between gap-4 py-2 text-sm">
          <span className="truncate">
            {ticket.reference && (
              <span className="text-muted-foreground mr-2">
                {ticket.reference}
              </span>
            )}
            <span>{ticket.subject}</span>
          </span>
          <span
            className={
              ticket.is_open ? "shrink-0" : "shrink-0 text-muted-foreground"
            }
          >
            {ticket.status}
          </span>
        </li>
      ))}
    </ul>
  );
};

const ContactRows = ({
  mainContactId,
}: {
  mainContactId: Identifier | null;
}) => {
  const { data: contacts, isPending } = useListContext<
    Contact & { role?: string }
  >();
  if (isPending) return null;
  if (!contacts?.length) return <NoneYet />;
  const sorted = [...contacts].sort(
    (a, b) =>
      Number(String(b.id) === String(mainContactId)) -
      Number(String(a.id) === String(mainContactId)),
  );
  return (
    <ul className="divide-y">
      {sorted.map((contact) => {
        const isMain = String(contact.id) === String(mainContactId);
        return (
          <li key={contact.id}>
            <Link
              to={`/contacts/${contact.id}/show`}
              className="flex items-center justify-between gap-4 py-2 text-sm hover:bg-muted/50"
            >
              <span className="flex items-center gap-1.5 min-w-0">
                {isMain && (
                  <Star
                    className="size-3.5 fill-current text-warning shrink-0"
                    aria-label="Main contact"
                  />
                )}
                <span className="truncate">
                  {contact.first_name} {contact.last_name}
                </span>
              </span>
              <span className="text-muted-foreground truncate">
                {contact.role || contact.email_jsonb?.[0]?.email || ""}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

/** One "label: value" row; empty values show a dash so missing data is visible. */
const DetailRow = ({ field, value }: { field: string; value: ReactNode }) => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">
        {translate(`resources.companies.fields.${field}`)}
      </dt>
      <dd className="text-sm break-words whitespace-pre-line">
        {value || <EmptyValue />}
      </dd>
    </div>
  );
};

const DetailCard = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Card>
    <CardContent className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold">{title}</h2>
      <dl className="flex flex-col gap-3">{children}</dl>
    </CardContent>
  </Card>
);

const websiteHref = (url: string) =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

const ContactDetails = ({ account }: { account: ZcrmAccount }) => (
  <DetailCard title="Details">
    <DetailRow
      field="address"
      value={[
        account.address,
        [account.city, [account.state_abbr, account.zipcode].join(" ").trim()]
          .filter(Boolean)
          .join(", "),
      ]
        .filter(Boolean)
        .join("\n")}
    />
    <DetailRow
      field="office_phone"
      value={
        account.office_phone && (
          <a href={`tel:${account.office_phone}`} className="underline">
            {account.office_phone}
          </a>
        )
      }
    />
    <DetailRow
      field="website"
      value={
        account.website && (
          <a
            href={websiteHref(account.website)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 underline"
          >
            {account.website.replace(/^https?:\/\//i, "")}
            <ExternalLink className="size-3" />
          </a>
        )
      }
    />
    <DetailRow
      field="email"
      value={
        account.email && (
          <a href={`mailto:${account.email}`} className="underline">
            {account.email}
          </a>
        )
      }
    />
  </DetailCard>
);

const EngagementDetails = ({ account }: { account: ZcrmAccount }) => (
  <DetailCard title="Engagement">
    <DetailRow field="signed_on" value={account.signed_on?.slice(0, 10)} />
    <DetailRow field="agreement_state" value={account.agreement_state} />
    <DetailRow field="main_contact_name" value={account.main_contact_name} />
    <DetailRow
      field="expected_monthly_orders"
      value={account.expected_monthly_orders}
    />
  </DetailCard>
);

const ReferenceDetails = ({ account }: { account: ZcrmAccount }) => (
  <DetailCard title="Account details">
    <DetailRow field="doors_served" value={account.doors_served} />
    <DetailRow field="serviceable_doors" value={account.serviceable_doors} />
    <DetailRow field="markets_served" value={account.markets_served} />
    <DetailRow field="pm_software" value={account.pm_software} />
    <DetailRow field="lead_source" value={account.lead_source} />
    <DetailRow field="referred_by" value={account.referred_by} />
    <DetailRow field="former_names" value={account.former_names} />
    <DetailRow field="pipedrive_id" value={account.pipedrive_id} />
    <DetailRow field="notes" value={account.notes} />
    {account.account_details.map(({ label, value }) => (
      <div key={label} className="flex flex-col gap-0.5">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="text-sm break-words">{value || <EmptyValue />}</dd>
      </div>
    ))}
  </DetailCard>
);
